import {
  BookOpen,
  BookX,
  Brain,
  Eye,
  Megaphone,
  ClipboardCheck,
  PencilRuler,
  Repeat,
  ArrowRight,
  ArrowDown,
  RotateCcw,
} from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { LOOP_STEPS } from "@/data/content";

const STEP_ICONS = [BookOpen, BookX, Brain, Eye, Megaphone, ClipboardCheck, PencilRuler, Repeat];

const LearningLoop = () => (
  <section id="learning-loop" data-testid="learning-loop-section" className="py-20 lg:py-28 bg-brand-light scroll-mt-24">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeading
        align="center"
        eyebrow="The IQMIZE Learning Loop"
        title="Move beyond reading and rereading."
        description="A central part of IQMIZE is learning to retrieve, not just review. Follow the loop step by step — then come back around again."
      />

      <div className="relative mt-16 max-w-xl mx-auto lg:max-w-none" data-testid="learning-loop-diagram">
        {/* Vertical guide line — mobile only */}
        <div className="absolute left-7 top-8 bottom-8 w-0.5 bg-brand/25 lg:hidden" aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-x-10 lg:gap-y-16">
          {LOOP_STEPS.map((step, i) => {
            const Icon = STEP_ICONS[i];
            const isRepeat = i === LOOP_STEPS.length - 1;
            const hasRightArrow = i % 4 !== 3;
            return (
              <Reveal key={step.title} delay={(i % 4) * 0.08} className="relative">
                {/* Arrow to next step — desktop rows */}
                {hasRightArrow && (
                  <div className="hidden lg:flex absolute top-1/2 -right-8 -translate-y-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-white border border-brand/30 text-brand shadow-sm" aria-hidden="true">
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </div>
                )}
                {/* Arrow down to next row — desktop, end of first row */}
                {i === 3 && (
                  <div className="hidden lg:flex absolute -bottom-12 left-1/2 -translate-x-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-white border border-brand/30 text-brand shadow-sm" aria-hidden="true">
                    <ArrowDown size={16} strokeWidth={2.5} />
                  </div>
                )}

                <div
                  data-testid={`loop-step-${i + 1}`}
                  className={`relative flex lg:block items-center gap-5 rounded-2xl border p-5 lg:p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] ${
                    isRepeat
                      ? "border-igold bg-white shadow-[0_8px_30px_rgb(245,166,35,0.15)]"
                      : "border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                  }`}
                >
                  <div className="relative shrink-0 lg:shrink">
                    <div
                      data-testid={`loop-step-icon-${i + 1}`}
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
                        isRepeat ? "bg-igold text-slate-900" : "bg-brand text-white"
                      }`}
                    >
                      <Icon size={26} strokeWidth={1.8} />
                    </div>
                    <span
                      className={`absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[11px] font-extrabold ${
                        isRepeat ? "bg-inavy text-white" : "bg-igold text-slate-900"
                      }`}
                    >
                      {i + 1}
                    </span>
                  </div>
                  <div className="lg:mt-4">
                    <h3 className="text-base lg:text-lg font-bold tracking-tight text-slate-900 uppercase">{step.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Loop-back connector */}
        <Reveal delay={0.15} className="mt-10 lg:mt-14 flex justify-center">
          <div
            data-testid="loop-back-connector"
            className="inline-flex items-center gap-3 rounded-full border-2 border-dashed border-igold bg-white px-6 py-3.5 shadow-sm"
          >
            <RotateCcw size={18} className="text-igold" strokeWidth={2.2} />
            <p className="text-sm font-bold text-inavy">
              Repeat Later loops back to Step 1 — spaced return is what makes learning stick.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default LearningLoop;
